import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikProps } from 'formik';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchRerpot, DataState } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { useApiResponseError } from '../../../global/hooks/useApiError';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { FormField } from '../../../global/components/form-element/form-field';
import { FormTypes } from '../../account-info/models/form-models';
import { ApiStatuses } from '../../../global/models/api-model';
import { patchClergyBioChurchReport } from '../../../global/ducks/clergy-bio-api';
import { setClergyBioChurchReport } from '../clergy-bio-slice';
import { ClergyBioChurchReportingFormModel, ethnicityOptions, genderIdentityOptions, sexualOrientationOptions } from './model.ts/form-model';
import { ConstructPayload, GetClergyBioChurchReportingValidationSchema } from './clergy-bio-church-reporting-helper';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { personInfoSelfDescribeValue } from '../../../global/validation-schemas/account-information-form-validation';
import { PageHeader } from '../../../global/components/layout/page-header';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';



function ClergyBioChurchReportingUpdate() {

	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.churchReport);
	const churchReporting = useSelector<TcsmStates, ClergyBioChurchRerpot>(state => state?.clergyBioState?.churchReport);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const dispatch = useDispatch();
	const initialValues: ClergyBioChurchReportingFormModel = {
		gender_identities: churchReporting?.details?.genderIdentities || [],
		race_ethnicities: churchReporting?.details?.ethnicities || [],
		sexual_orientations: churchReporting?.details?.sexualOrientations || [],
		sexualSpecify: churchReporting?.details?.sexualSpecify,
		ethnicitySpecify: churchReporting?.details?.ethnicitySpecify,
		genderSpecify: churchReporting?.details?.genderSpecify,
	};
	const apiErrors = useApiResponseError();
	const validationSchema = GetClergyBioChurchReportingValidationSchema(settings);
	const onSuccess = (res) => {
		HandleClergyBioSubmit(ClergyBioGaFormTypes.ChurchReporting, guid);
		setUpdateStatus(ApiStatuses.Success);
		dispatch(setClergyBioChurchReport(res?.data));
	};

	const updateChurchReporting = (values: ClergyBioChurchReportingFormModel) => {
		dispatch(patchClergyBioChurchReport(guid, ConstructPayload(values), onSuccess));
	};


	const onChange = (e, formValues, key, fieldArrayProps: FieldArrayRenderProps) => {
		const values = formValues[key];
		const { value } = e.target;
		if (e.target.checked) {
			fieldArrayProps.push(value);
		} else {
			fieldArrayProps.remove(values.indexOf(value));
		}
	};

	function renderContent() {
		return (
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updateChurchReporting(values)}
				enableReinitialize
				initialErrors={apiErrors}
				validationSchema={validationSchema}
			>
				{(formProps: FormikProps<ClergyBioChurchReportingFormModel>) => (

					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								formProps.resetForm();
								window.scrollTo(0, 0);
							}}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_CHURCH_REPORTING_UPDATE] : []}
						>
							<DividerSection removeDivider sectionText={settings?.churchReportingSubTitle} sectionDescription={settings?.churchReportingDescription} >
								<ContentSpacer>
									<FieldArray
										name='race_ethnicities'
										render={(fieldArrayProps) => (
											<>
												<h5>{settings?.raceLabel} {settings?.checkAllApplyLabel}</h5>
												{
													ethnicityOptions.map(r => {
														return (
															<FormField
																id={r.value}
																name='race_ethnicities'
																label={r.label}
																component='checkBox'
																touched={formProps.touched}
																errors={formProps.errors}
																value={r.value}
																checked={formProps.values.race_ethnicities?.includes(r.value)}
																checkBoxGroup
																onChange={(e) => onChange(e, formProps?.values, 'race_ethnicities', fieldArrayProps)}

															/>
														);
													})
												}
											</>
										)}
									/>
									{
										formProps.values.race_ethnicities?.includes(personInfoSelfDescribeValue) &&
										<FormField
											name='ethnicitySpecify'
											label={settings?.ethnicitiesSelfDescribeLabel}
											component='input'
											touched={formProps.touched}
											errors={formProps.errors}
											required
										/>
									}

								</ContentSpacer>

								<ContentSpacer>
									<FieldArray
										name='sexual_orientations'
										render={(fieldArrayProps) => (
											<>
												<h5>{settings?.sexualOrientationLabel} {settings?.checkAllApplyLabel}</h5>

												{
													sexualOrientationOptions.map(s => {
														return (
															<FormField
																id={s.value}
																name='sexual_orientations'
																label={s.label}
																component='checkBox'
																touched={formProps.touched}
																errors={formProps.errors}
																value={s.value}
																checked={formProps.values.sexual_orientations?.includes(s.value)}
																checkBoxGroup
																onChange={(e) => onChange(e, formProps?.values, 'sexual_orientations', fieldArrayProps)}

															/>
														);
													})
												}
											</>
										)}
									/>
									{
										formProps.values.sexual_orientations?.includes(personInfoSelfDescribeValue) &&
										<FormField
											name='sexualSpecify'
											label={settings?.sexualSelfDescribeLabel}
											component='input'
											touched={formProps.touched}
											errors={formProps.errors}
											required
										/>
									}
								</ContentSpacer>

								<FieldArray
									name='gender_identities'
									render={(fieldArrayProps) => (
										<>

											<h5>{settings.genderIdentityLabel} {settings?.checkAllApplyLabel}</h5>

											{
												genderIdentityOptions.map(gender => {
													return (
														<FormField
															id={gender.value}
															name='gender_identities'
															label={gender.label}
															component='checkBox'
															touched={formProps.touched}
															errors={formProps.errors}
															value={gender.value}
															checked={formProps.values.gender_identities?.includes(gender.value)}
															checkBoxGroup
															onChange={(e) => onChange(e, formProps?.values, 'gender_identities', fieldArrayProps)}

														/>
													);
												})
											}
										</>
									)}
								/>
								{
									formProps.values.gender_identities?.includes(personInfoSelfDescribeValue) &&
									<FormField
										name='genderSpecify'
										label={settings?.genderSelfDescribeLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
										required
									/>
								}
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
		);
	}

	return (
		<>
			<PageHeader headerText={settings?.churchReportingTitle} />
			<ClergyBioComponentLoader
				state={loadingState}
				content={renderContent()}
			/>
		</>
	);
}


export default ClergyBioChurchReportingUpdate;